// The mock/real seam (task [3.2]). ONE hook signature regardless of fetcher.
// When VITE_API_URL is unset/empty the sync mock backs each query; otherwise the
// real fetcher does. No page component branches on mock-vs-real — this file is
// the only place that decision lives.

import {
	useInfiniteQuery,
	useMutation,
	useQueryClient,
	useQuery,
} from '@tanstack/react-query';

import { env } from '@/env';
import {
	getWebhookConfigMock,
	getWebhookConfigsMock,
	getWebhookEventsMock,
	replayWebhookEventMock,
	type WebhookConfig,
	type WebhookConfigPage,
	type WebhookEvent,
	type WebhookEventPage,
} from './mock';
import {
	getWebhookConfig,
	getWebhookConfigs,
	getWebhookEvents,
	replayWebhookEvent,
} from './real';

export const webhookConfigQueryKey = ['webhooks', 'config'] as const;
export const webhookConfigsQueryKey = ['webhooks', 'configs'] as const;
export const webhookEventsQueryKey = ['webhooks', 'events'] as const;

const CONFIGS_PAGE_SIZE = 20;
const EVENTS_PAGE_SIZE = 20;

/** Single-record query for the primary endpoint configuration. */
export function useWebhookConfig() {
	return useQuery<WebhookConfig>({
		queryKey: webhookConfigQueryKey,
		queryFn: () =>
			env.VITE_API_URL ? getWebhookConfig() : getWebhookConfigMock(),
	});
}

/**
 * Cursor-paginated list of endpoint configs for the webhooks index page.
 * `data.pages` is an array of `WebhookConfigPage`; flatten with
 * `data.pages.flatMap((p) => p.configs)`. Same shape as `useWebhookEvents()`.
 */
export function useWebhookConfigs() {
	return useInfiniteQuery<WebhookConfigPage, Error>({
		queryKey: webhookConfigsQueryKey,
		queryFn: ({ pageParam }) => {
			const cursor = pageParam as string | null;
			return env.VITE_API_URL
				? getWebhookConfigs({ cursor, limit: CONFIGS_PAGE_SIZE })
				: getWebhookConfigsMock({ cursor, limit: CONFIGS_PAGE_SIZE });
		},
		initialPageParam: null as string | null,
		getNextPageParam: (lastPage) => lastPage.nextCursor,
	});
}

/**
 * Cursor-paginated event feed for infinite-scroll. `data.pages` is an array of
 * `WebhookEventPage`; flatten with `data.pages.flatMap((p) => p.events)`. Drive
 * loading with `fetchNextPage()` / `hasNextPage` / `isFetchingNextPage`.
 */
export function useWebhookEvents() {
	return useInfiniteQuery<WebhookEventPage, Error>({
		queryKey: webhookEventsQueryKey,
		queryFn: ({ pageParam }) => {
			const cursor = pageParam as string | null;
			return env.VITE_API_URL
				? getWebhookEvents({ cursor, limit: EVENTS_PAGE_SIZE })
				: getWebhookEventsMock({ cursor, limit: EVENTS_PAGE_SIZE });
		},
		initialPageParam: null as string | null,
		getNextPageParam: (lastPage) => lastPage.nextCursor,
	});
}

/**
 * Re-deliver an event. On success the events feed is invalidated so both the
 * list row and the detail pane reflect the incremented attempt / fresh status.
 */
export function useReplayWebhookEvent() {
	const queryClient = useQueryClient();

	return useMutation<WebhookEvent, Error, string>({
		mutationFn: (id) =>
			env.VITE_API_URL
				? replayWebhookEvent(id)
				: Promise.resolve(replayWebhookEventMock(id)),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: webhookEventsQueryKey });
		},
	});
}
